/**
 * projectGroupedSidebar.logic.ts — pure grouping logic for the canonical P3
 * project-first sidebar
 * (spec: docs/specs/2026-07-24-project-grouped-sidebar-and-sprite-workspaces.md §P3).
 *
 * The caller supplies one environment-scoped project snapshot for each local,
 * remote desktop, or cloud copy. This module appends not-yet-connected cloud
 * catalog rows without folding them into another environment's project row.
 *
 * Each resulting `ProjectGroup` carries its own thread list and project-level
 * environment identity. Thread-origin metadata remains available for routing
 * tests, but the renderer deliberately keeps environment chrome off threads.
 *
 * No React imports — every function here is a pure transform, matching the
 * sibling `*.logic.ts` modules in this directory.
 */

import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import type { EnvironmentId, ScopedProjectRef } from "@cmmd-forge/contracts";

import type { CloudProjectCatalogRow } from "../../cmmd/cmmdForgeRuntimeListResource";
import type { SidebarStatusFilter } from "./sidebarViewFilters";
import type { SidebarThreadSummary } from "../../types";
import {
  mergeSidebarProjectMemberRefs,
  synthesizePreConnectCloudSnapshot,
} from "./cloudProjectsGroup.logic";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import type { SidebarProjectSnapshot, SidebarProjectWorktreeMember } from "./SidebarProjectItem";
import {
  runtimeScopedSidebarKey,
  type SavedEnvironmentRuntimeIndex,
} from "./sidebarThreadRuntimeIdentity";
import { computeVisibleProjectThreads } from "./useSidebarProjectThreadData.logic";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ThreadEnvironmentKind = "cloud" | "desktop";
export type ProjectEnvironmentKind = ThreadEnvironmentKind | "mixed";

export interface GroupedThreadRow {
  readonly thread: SidebarThreadSummary;
  readonly environmentKind: ThreadEnvironmentKind;
  readonly environmentLabel: string;
  readonly projectCwd: string | null;
}

export interface ProjectGroup {
  readonly groupKey: string;
  readonly displayName: string;
  readonly iconUrl: string | null;
  readonly icon: string | null;
  readonly environmentKind: ProjectEnvironmentKind;
  readonly environmentLabel: string;
  readonly representative: SidebarProjectSnapshot;
  readonly memberProjectRefs: readonly ScopedProjectRef[];
  readonly threads: readonly GroupedThreadRow[];
}

/**
 * Whether CMMD owns this project's icon. A connected project carries its
 * canonical fields explicitly, a not-yet-connected cloud row carries its
 * catalog metadata, and a plain local checkout carries neither — the last is
 * the only case where the favicon under `cwd` is the right thing to draw.
 */
function hasCanonicalProjectIdentity(group: ProjectGroup): boolean {
  return (
    group.representative.canonicalIconUrl !== undefined ||
    group.representative.canonicalIcon !== undefined ||
    group.representative.preConnectCloud != null
  );
}

/**
 * The group's canonical icon, or `undefined` when the favicon path owns it.
 * Every surface that draws a project — sidebar rows, the Activity picker —
 * resolves it through here so they cannot drift into disagreeing about which
 * provenance wins.
 */
export function resolveCanonicalProjectIdentity(
  group: ProjectGroup,
): { readonly iconUrl: string | null; readonly icon: string | null } | undefined {
  return hasCanonicalProjectIdentity(group)
    ? { iconUrl: group.iconUrl, icon: group.icon }
    : undefined;
}

/**
 * The CMMD board this group belongs to, or `null` when the row cannot answer.
 *
 * Cloud rows know it synchronously — a pre-connect row carries its catalog
 * metadata, a connected one its proven registration — and CMMD uses the same
 * id for the planning project the board renders. A local checkout's link is
 * keyed by `cwd` and only knowable through a read, so `null` here means
 * "unknown", not "unlinked"; callers that need certainty resolve the cwd link.
 *
 * Read for NAVIGATION only. The removal path deliberately refuses to trust any
 * id a row carries (see `sidebarProjectRemoval.ts`) — that fence is about
 * destructive targets and this helper must never be wired into it.
 */
export function resolveCanonicalProjectCmmdId(group: ProjectGroup): number | null {
  const representative = group.representative;
  if (typeof representative.preConnectCloud?.cmmdProjectId === "number") {
    return representative.preConnectCloud.cmmdProjectId;
  }
  return typeof representative.cloudRegistrationProjectId === "number"
    ? representative.cloudRegistrationProjectId
    : null;
}

export interface BuildProjectGroupsInput {
  /**
   * Environment-scoped desktop, connected-cloud, and inventory projects. A
   * matching repo on two environments remains two project rows.
   */
  readonly mergedProjects: readonly SidebarProjectSnapshot[];
  /** Cloud catalog rows whose runtime has not (yet) connected. */
  readonly preConnectCloudRows: readonly CloudProjectCatalogRow[];
  /** Inventory refs retained for catalog rows whose runtime is currently offline. */
  readonly preConnectCloudMemberRefsByRuntimeId?: ReadonlyMap<string, readonly ScopedProjectRef[]>;
  readonly allSidebarThreads: readonly SidebarThreadSummary[];
  readonly resolveEnvironmentLabel: (environmentId: EnvironmentId) => string;
  /**
   * Which threads the sidebar's Status control is asking for. Optional and
   * defaulting to "active" so existing callers keep hiding archived threads.
   */
  readonly statusFilter?: SidebarStatusFilter;
  /**
   * The saved Environments, so a thread can be matched to its group by the
   * runtime behind its key rather than by the key itself. Omitted, matching
   * stays on raw `(environmentId, projectId)` pairs.
   */
  readonly savedEnvironmentRegistry?: SavedEnvironmentRuntimeIndex;
}

// ---------------------------------------------------------------------------
// Thread → execution-origin classification (not rendered as thread chrome)
// ---------------------------------------------------------------------------

function findMember(
  members: readonly SidebarProjectWorktreeMember[],
  thread: Pick<SidebarThreadSummary, "environmentId" | "projectId">,
): SidebarProjectWorktreeMember | undefined {
  return members.find(
    (member) =>
      member.environmentId === thread.environmentId && member.projectId === thread.projectId,
  );
}

/** Resolves a thread's `environmentRole`: the owning member's role when one was
 * found by `findMember`, else inferred from whether the thread's own
 * environment id classifies as cloud (a legacy runtime id or a durable `env_`
 * Environment). */
function resolveThreadEnvironmentRole(
  member: SidebarProjectWorktreeMember | undefined,
  thread: Pick<SidebarThreadSummary, "environmentId">,
): SidebarProjectWorktreeMember["environmentRole"] {
  return (
    member?.environmentRole ??
    (classifyEnvironmentId(thread.environmentId).isCloud ? "cloud" : "local")
  );
}

function resolveThreadEnvironmentKind(
  member: SidebarProjectWorktreeMember | undefined,
  thread: Pick<SidebarThreadSummary, "environmentId">,
): ThreadEnvironmentKind {
  return resolveThreadEnvironmentRole(member, thread) === "cloud" ? "cloud" : "desktop";
}

/** The thread's own cwd when its owning member carries one, else the
 * representative project's cwd — but only when the thread IS the
 * representative project's own thread (same environment + project id). */
function resolveThreadProjectCwd(
  member: SidebarProjectWorktreeMember | undefined,
  thread: Pick<SidebarThreadSummary, "environmentId" | "projectId">,
  representative: SidebarProjectSnapshot,
): string | null {
  const isRepresentativeThread =
    thread.environmentId === representative.environmentId && thread.projectId === representative.id;
  return member?.cwd ?? (isRepresentativeThread ? representative.cwd : null);
}

function resolveThreadEnvironmentLabel(
  environmentKind: ThreadEnvironmentKind,
  thread: Pick<SidebarThreadSummary, "environmentId">,
  resolveEnvironmentLabel: (environmentId: EnvironmentId) => string,
): string {
  return environmentKind === "cloud" ? "Cloud" : resolveEnvironmentLabel(thread.environmentId);
}

function buildGroupedThreadRow(
  thread: SidebarThreadSummary,
  representative: SidebarProjectSnapshot,
  resolveEnvironmentLabel: (environmentId: EnvironmentId) => string,
): GroupedThreadRow {
  const member = findMember(representative.worktreeMembers, thread);
  const environmentKind = resolveThreadEnvironmentKind(member, thread);
  const projectCwd = resolveThreadProjectCwd(member, thread, representative);
  const environmentLabel = resolveThreadEnvironmentLabel(
    environmentKind,
    thread,
    resolveEnvironmentLabel,
  );
  return { thread, environmentKind, environmentLabel, projectCwd };
}

/**
 * The threads a group owns, matched by runtime identity rather than by raw
 * environment key.
 *
 * One cloud runtime reaches the sidebar under two keys. CMMD's replica and the
 * sidebar's own cloud rows name it by the legacy locator, while a live
 * connection registers it under the durable `env_` Environment (FOR-32652), and
 * the saved-environment registry is what says the two are the same Sprite.
 * Comparing raw `(environmentId, projectId)` pairs is FOR-36687 in the one
 * place it was still open: a thread sent under the durable key attached to no
 * group at all, so it rendered nowhere and left the ⌘1..⌘9 jump set.
 *
 * Desktop rows are untouched. `runtimeScopedSidebarKey` falls back to the plain
 * `environmentId:id` form whenever no runtime answers for the key, so a group
 * with no runtime behind it compares exactly what it compared before.
 */
function selectRuntimeScopedGroupThreads(
  threads: readonly SidebarThreadSummary[],
  refs: readonly ScopedProjectRef[],
  environments: SavedEnvironmentRuntimeIndex,
): readonly SidebarThreadSummary[] {
  const ownedKeys = new Set(
    refs.map((ref) => runtimeScopedSidebarKey(ref.environmentId, ref.projectId, environments)),
  );
  return threads.filter((thread) =>
    ownedKeys.has(runtimeScopedSidebarKey(thread.environmentId, thread.projectId, environments)),
  );
}

function buildGroupThreads(
  group: Pick<ProjectGroup, "representative" | "memberProjectRefs">,
  allSidebarThreads: readonly SidebarThreadSummary[],
  resolveEnvironmentLabel: (environmentId: EnvironmentId) => string,
  statusFilter: SidebarStatusFilter,
  savedEnvironmentRegistry: SavedEnvironmentRuntimeIndex,
): readonly GroupedThreadRow[] {
  const projectRef = scopeProjectRef(group.representative.environmentId, group.representative.id);
  const ownedThreads = selectRuntimeScopedGroupThreads(
    allSidebarThreads,
    [projectRef, ...group.memberProjectRefs],
    savedEnvironmentRegistry,
  );
  const visibleThreads = computeVisibleProjectThreads(ownedThreads, undefined, statusFilter);
  return visibleThreads.map((thread) =>
    buildGroupedThreadRow(thread, group.representative, resolveEnvironmentLabel),
  );
}

// ---------------------------------------------------------------------------
// buildProjectGroups
// ---------------------------------------------------------------------------

interface GroupSeed {
  readonly groupKey: string;
  readonly displayName: string;
  readonly iconUrl: string | null;
  readonly icon: string | null;
  readonly environmentKind: ProjectEnvironmentKind;
  readonly environmentLabel: string;
  readonly representative: SidebarProjectSnapshot;
  readonly memberProjectRefs: readonly ScopedProjectRef[];
}

function resolveProjectEnvironmentKind(project: SidebarProjectSnapshot): ProjectEnvironmentKind {
  const representativeIsCloud = Boolean(
    project.preConnectCloud ||
    project.source?.workSource === "cloud-vm" ||
    classifyEnvironmentId(project.environmentId).isCloud,
  );
  const hasCloudMember = project.worktreeMembers.some(
    (member) => member.environmentRole === "cloud",
  );
  const hasDesktopMember = project.worktreeMembers.some(
    (member) => member.environmentRole !== "cloud",
  );
  const hasCloud = representativeIsCloud || hasCloudMember;
  const hasDesktop = !representativeIsCloud || hasDesktopMember;

  if (hasCloud && hasDesktop) return "mixed";
  return hasCloud ? "cloud" : "desktop";
}

function resolveProjectEnvironmentLabel(
  project: SidebarProjectSnapshot,
  environmentKind: ProjectEnvironmentKind,
  resolveEnvironmentLabel: BuildProjectGroupsInput["resolveEnvironmentLabel"],
): string {
  if (environmentKind === "mixed") return "Available locally and in Cloud";
  return environmentKind === "cloud" ? "Cloud" : resolveEnvironmentLabel(project.environmentId);
}

function seedFromProject(
  project: SidebarProjectSnapshot,
  resolveEnvironmentLabel: BuildProjectGroupsInput["resolveEnvironmentLabel"],
): GroupSeed {
  const environmentKind = resolveProjectEnvironmentKind(project);
  return {
    groupKey: project.projectKey,
    displayName: project.name,
    iconUrl: project.canonicalIconUrl ?? project.preConnectCloud?.iconUrl ?? null,
    icon: project.canonicalIcon ?? project.preConnectCloud?.icon ?? null,
    environmentKind,
    environmentLabel: resolveProjectEnvironmentLabel(
      project,
      environmentKind,
      resolveEnvironmentLabel,
    ),
    representative: project,
    memberProjectRefs: project.memberProjectRefs,
  };
}

/**
 * Builds the ordered list of `ProjectGroup`s for the project-grouped sidebar.
 *
 * Ordering: one group per `mergedProjects` entry, in input order (already
 * sorted by `sortProjectsForSidebar` upstream), followed by every distinct
 * not-yet-connected cloud catalog project in `preConnectCloudRows` order.
 */
export function buildProjectGroups(input: BuildProjectGroupsInput): readonly ProjectGroup[] {
  const seenStandaloneCloudKeys = new Set<string>();
  const standaloneCloudSeeds: GroupSeed[] = [];

  for (const row of input.preConnectCloudRows) {
    const catalogRepresentative = synthesizePreConnectCloudSnapshot(row);
    const inventoryMemberRefs =
      row.runtimeId === null
        ? []
        : (input.preConnectCloudMemberRefsByRuntimeId?.get(row.runtimeId) ?? []);
    const representative = mergeSidebarProjectMemberRefs(
      catalogRepresentative,
      inventoryMemberRefs,
    );
    if (seenStandaloneCloudKeys.has(representative.projectKey)) continue;
    seenStandaloneCloudKeys.add(representative.projectKey);
    standaloneCloudSeeds.push(seedFromProject(representative, input.resolveEnvironmentLabel));
  }

  const seeds = [
    ...input.mergedProjects.map((project) =>
      seedFromProject(project, input.resolveEnvironmentLabel),
    ),
    ...standaloneCloudSeeds,
  ];

  return seeds.map((seed) =>
    Object.assign(seed, {
      threads: buildGroupThreads(
        seed,
        input.allSidebarThreads,
        input.resolveEnvironmentLabel,
        input.statusFilter ?? "active",
        input.savedEnvironmentRegistry ?? {},
      ),
    }),
  );
}
